"use client";

// ===================================
// ENHANCED CHAT PAGE WITH SESSION MANAGEMENT - src/pages/Chat.tsx
// ===================================
import type React from "react";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { chatApi, searchApi, agenticAuthApi } from "../services/api";
import Header from "../components/Header";
import LoadingSpinner from "../components/LoadingSpinner";
import AgenticChecklist from "../components/AgenticChecklist";
import AgenticResponse from "../components/AgenticResponse";
import {
  Send,
  RotateCcw,
  Search,
  Settings,
  Plus,
  MessageSquare,
  Archive,
  MoreVertical,
  Edit2,
  Trash2,
  Download,
} from "lucide-react";
import type { ChatSession, Message, SearchStats } from "../types";
import { notifyError, notifySuccess } from "../utils/Toastify";
import { Link } from "react-router-dom";
import { chatHelpers, sessionManager } from "../services/api";
import ReactMarkdown from "react-markdown";
import AiChatIcon from "../assets/aiChatIcon.png";

const Chat: React.FC = () => {
  const { user } = useAuth();
  const { hideChatSidebar } = useSettings();

  // Chat state
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(
    null
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);

  // UI state
  const [showSources, setShowSources] = useState<{
    [messageId: string]: boolean;
  }>({});
  const [showSessionMenu, setShowSessionMenu] = useState<{
    [sessionId: string]: boolean;
  }>({});
  const [editingSession, setEditingSession] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");

  // RAG settings
  const [ragMode, setRagMode] = useState(true);
  const [ragSettings, setRagSettings] = useState({
    temperature: 0.1,
    include_citations: true,
    confidence_threshold: 0.7,
    max_tokens: 2000,
  });

  // Search stats
  const [searchStats, setSearchStats] = useState<SearchStats | null>(null);

  // Agentic auth state
  const [agenticAuthState, setAgenticAuthState] = useState({
    showModal: false,
    loading: false,
    error: "",
    pendingAction: null as any,
    code: "manifest-123456",
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { hideChatMode, hideExport, hideSearch, debugMode } = useSettings();

  useEffect(() => {
    initializeChat();
    loadSearchStats();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    // Check if user is already agentic-verified
    if (sessionManager.isAgenticVerified()) {
    }
  }, []);

  const initializeChat = async () => {
    try {
      setIsLoadingSessions(true);
      await loadSessions();
    } catch (error) {
      notifyError("Failed to load chat sessions");
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await chatApi.getSessions({ limit: 50 });
      setSessions(response.data.sessions || response.data || []);

      // Load the most recent session
      if (
        !currentSession &&
        (response.data.sessions || response.data).length > 0
      ) {
        const mostRecent = (response.data.sessions || response.data)[0];
        setCurrentSession(mostRecent);
        await loadSessionMessages(mostRecent.id);
      }
    } catch (error) {}
  };

  const loadSessionMessages = async (sessionId: string) => {
    try {
      const response = await chatApi.getSession(sessionId, {
        message_limit: 50,
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      setMessages([]);
    }
  };

  const loadSearchStats = async () => {
    try {
      const response = await searchApi.stats();
      setSearchStats(response.data.search_analytics || response.data.data);
    } catch (error) {
      setSearchStats(null);
    }
  };

  const createNewSession = async (
    title?: string,
    type: "general" | "document_qa" | "rag_chat" = "rag_chat"
  ) => {
    try {
      const response = await chatApi.createSession({
        title: title || "New Conversation",
        type,
        context_documents: [],
      });

      const newSession = response.data.session;
      setSessions((prev) => [newSession, ...prev]);
      setCurrentSession(newSession);
      setMessages([]);

      notifySuccess("Created new conversation");

      return newSession;
    } catch (error) {
      notifyError("Failed to create new chat session");
    }
  };

  const switchSession = async (session: ChatSession) => {
    if (session.id === currentSession?.id) return;

    setCurrentSession(session);
    setIsLoading(true);
    try {
      await loadSessionMessages(session.id);
    } finally {
      setIsLoading(false);
    }
  };

  const updateSessionTitle = async (sessionId: string, newTitle: string) => {
    try {
      await chatApi.updateSession(sessionId, { title: newTitle });
      setSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? { ...s, title: newTitle } : s))
      );

      if (currentSession?.id === sessionId) {
        setCurrentSession((prev) =>
          prev ? { ...prev, title: newTitle } : null
        );
      }

      setEditingSession(null);
      setEditTitle("");
    } catch (error) {
      notifyError("Failed to update session title");
    }
  };

  const deleteSession = async (sessionId: string) => {
    if (!window.confirm("Are you sure you want to delete this conversation?")) {
      return;
    }

    try {
      await chatApi.deleteSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));

      if (currentSession?.id === sessionId) {
        const remainingSessions = sessions.filter((s) => s.id !== sessionId);
        if (remainingSessions.length > 0) {
          switchSession(remainingSessions[0]);
        } else {
          createNewSession();
        }
      }
      notifySuccess("Conversation has been deleted");
    } catch (error) {
      notifyError("Failed to delete conversation");
    }
  };

  const archiveSession = async (sessionId: string) => {
    try {
      await chatApi.updateSession(sessionId, { status: "archived" });
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId
            ? {
                ...s,
                status: "archived" as const,
              }
            : s
        )
      );
      notifySuccess("Conversation has been archived");
    } catch (error) {
      notifyError("Failed to archive conversation");
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || isLoading || !currentSession) return;

    const userMessageContent = newMessage.trim();
    setNewMessage("");
    setIsLoading(true);

    // Immediately show user's message
    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: userMessageContent,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      const messageData = {
        message: userMessageContent,
        use_rag: ragMode && searchStats?.canPerformSearch,
        search_options: {
          limit: 5,
          similarity_threshold: ragSettings.confidence_threshold,
        },
        temperature: ragSettings.temperature,
        include_citations: ragSettings.include_citations,
      };

      const response = await chatApi.sendMessage(
        currentSession.id,
        messageData
      );
      const assistantMessage: Message = response.data.assistant_message;

      setMessages((prev) => [...prev, assistantMessage]);

      // Update session metadata
      setSessions((prev) =>
        prev.map((s) =>
          s.id === currentSession.id
            ? {
                ...s,
                message_count: s.message_count + 2,
                last_message_at: new Date().toISOString(),
              }
            : s
        )
      );
    } catch (error: any) {
      // Handle agentic auth required
      if (chatHelpers.requiresAgenticAuth(error)) {
        setAgenticAuthState({
          showModal: true,
          loading: false,
          error: "",
          pendingAction: {
            sessionId: currentSession.id,
            messageData: {
              message: userMessageContent,
              use_rag: ragMode && searchStats?.canPerformSearch,
              search_options: {
                limit: 5,
                similarity_threshold: ragSettings.confidence_threshold,
              },
              temperature: ragSettings.temperature,
              include_citations: ragSettings.include_citations,
            },
          },
          code: "manifest-123456",
        });

        // Show system notice about auth requirement
        const authNotice: Message = {
          id: Date.now().toString() + "-auth",
          role: "system",
          content:
            "This action requires authentication. Please verify your access to continue.",
          created_at: new Date().toISOString(),
          metadata: {
            type: "agentic_notice",
          },
        };
        setMessages((prev) => [...prev, authNotice]);
      } else {
        // Handle other errors
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content:
            "I encountered an issue processing your request. Please try again.",
          created_at: new Date().toISOString(),
          metadata: { type: "error_response" },
        };
        setMessages((prev) => [...prev, errorMessage]);

        notifyError("Failed to send message. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAgenticVerification = async () => {
    if (!agenticAuthState.code.trim()) {
      setAgenticAuthState((prev) => ({
        ...prev,
        error: "Please enter your access code",
      }));
      return;
    }

    setAgenticAuthState((prev) => ({ ...prev, loading: true, error: "" }));

    try {
      const response = await agenticAuthApi.verify({
        accessKey: agenticAuthState.code.trim(),
      });

      if (response.data.ok) {
        // Store the verification state immediately
        sessionManager.setAgenticVerified(response.data.principalId);

        // Close modal and show success
        setAgenticAuthState({
          showModal: false,
          loading: false,
          error: "",
          pendingAction: null,
          code: "manifest-123456",
        });

        notifyError(
          "Access granted for 24 hours. You can now retry your action."
        );

        // Check if server provided replay data for automatic execution
        if (response.data.replay) {
          try {
            setIsLoading(true);

            const retryResponse = await chatApi.sendMessage(
              response.data.replay.sessionId,
              {
                message: response.data.replay.message,
                ...response.data.replay.originalRequest,
              }
            );

            const assistantMessage = retryResponse.data.assistant_message;
            setMessages((prev) => [...prev, assistantMessage]);

            notifySuccess("Your request has been processed automatically");
          } catch (retryError) {
            notifyError("Please try your action again manually");
          } finally {
            setIsLoading(false);
          }
        }
      } else {
        setAgenticAuthState((prev) => ({
          ...prev,
          loading: false,
          error: "Invalid access code. Please try again.",
        }));
      }
    } catch (error: any) {
      setAgenticAuthState((prev) => ({
        ...prev,
        loading: false,
        error: "Verification failed. Please try again.",
      }));
    }
  };

  const regenerateMessage = async () => {
    if (!currentSession) return;

    try {
      setIsLoading(true);

      // Remove last assistant message
      setMessages((prev) => {
        const lastIndex = prev.length - 1;
        if (lastIndex >= 0 && prev[lastIndex].role === "assistant") {
          return prev.slice(0, lastIndex);
        }
        return prev;
      });

      const response = await chatApi.regenerateMessage(currentSession.id, {
        use_rag: ragMode && searchStats?.canPerformSearch,
        temperature: ragSettings.temperature,
      });

      const newMessage: Message = response.data.assistant_message;
      setMessages((prev) => [...prev, newMessage]);
    } catch (error) {
      notifyError("Failed to regenerate response");
    } finally {
      setIsLoading(false);
    }
  };

  const exportConversation = async () => {
    if (!currentSession) return;

    try {
      const conversationData = {
        session: currentSession,
        messages: messages,
        exported_at: new Date().toISOString(),
      };

      const dataStr = JSON.stringify(conversationData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `conversation-${currentSession.id}-${
        new Date().toISOString().split("T")[0]
      }.json`;
      link.click();

      URL.revokeObjectURL(url);
      notifySuccess("Conversation exported successfully");
    } catch (error) {
      notifyError("Failed to export conversation");
    }
  };

  const renderMessage = (message: Message) => {
    const isUser = message.role === "user";
    const isRAGResponse = message.metadata?.type === "rag_response";
    const isAgenticNotice = message.metadata?.type === "agentic_notice";
    const isAgenticChecklist =
      message.metadata?.type === "agentic_parameter_collection";
    const isAgenticReady = message.metadata?.type === "agentic_ready";
    const isAgenticResult = message.metadata?.type === "agentic_action_result";

    return (
      <div
        key={message.id}
        className={`flex items-start space-x-4 mb-6 ${
          isUser ? "flex-row-reverse space-x-reverse" : ""
        }`}
      >
        {/* Avatar */}
        <div className="flex-shrink-0">
          {isUser ? (
            <div className="w-10 h-10 rounded-full bg-[#020617] flex items-center justify-center text-white text-lg shadow-lg">
              {user?.name?.charAt(0).toUpperCase() || "👤"}
            </div>
          ) : isAgenticNotice ? (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-red-500 flex items-center justify-center text-white text-lg shadow-lg">
              🔐
            </div>
          ) : isAgenticChecklist ? (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-500 flex items-center justify-center text-white text-lg shadow-lg">
              📝
            </div>
          ) : isAgenticReady ? (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white text-lg shadow-lg">
              ✅
            </div>
          ) : isAgenticResult ? (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-white text-lg shadow-lg">
              ⚡
            </div>
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#8b3dff] flex items-center justify-center text-white text-lg shadow-lg">
              <img src={AiChatIcon} alt="chat icon" className="bot-image" />
            </div>
          )}
        </div>

        {/* Message Content */}
        <div className={`max-w-xs lg:max-w-2xl `}>
          {isAgenticChecklist ? (
            <AgenticChecklist content={message.content} />
          ) : isAgenticResult ? (
            <AgenticResponse message={message} />
          ) : (
            <div
              className={`px-4 py-3 rounded-2xl shadow-sm ${
                isUser
                  ? "bg-[#8b3dff] text-white rounded-br-md"
                  : isRAGResponse
                  ? "bg-[#020617] border border-[#334155] text-white rounded-bl-md"
                  : isAgenticNotice
                  ? "bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-bl-md"
                  : isAgenticReady
                  ? "bg-green-50 border border-green-300 text-green-800 rounded-bl-md"
                  : "bg-[#020617] border border-[#334155] text-white rounded-bl-md"
              }`}
            >
              <div className="whitespace-pre-wrap leading-relaxed">
                <ReactMarkdown
                  components={{
                    strong: ({ node, ...props }) => (
                      <strong className="font-bold text-gray-100" {...props} />
                    ),
                    code: ({ node, ...props }) => (
                      <code
                        className="px-1 py-0.5 bg-gray-300 rounded text-sm"
                        {...props}
                      />
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>

              {/* Confidence Score for RAG responses */}
              {isRAGResponse &&
                message.metadata?.confidence_score !== undefined && (
                  <div className="mt-2 flex items-center space-x-2">
                    <span className="text-xs text-purple-300 font-medium">
                      Confidence:{" "}
                      {(message.metadata.confidence_score * 100).toFixed(1)}%
                    </span>
                    {message.metadata.confidence_score < 0.7 && (
                      <span className="text-xs text-yellow-400">
                        ⚠️ Low confidence
                      </span>
                    )}
                  </div>
                )}

              {/* Timestamp */}
              <div
                className={`text-xs mt-2 ${
                  isUser
                    ? "text-purple-200"
                    : isRAGResponse
                    ? "text-purple-400"
                    : "text-gray-500"
                }`}
              >
                {new Date(message.created_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                {isRAGResponse && <span className="ml-2">🧠 RAG</span>}
              </div>
            </div>
          )}

          {/* Generation Metadata */}
          {!isUser && message.metadata?.generation_metadata && (
            <div className="mt-1 text-xs text-[#64748b] flex items-center space-x-2">
              <span>{message.metadata.generation_metadata.model}</span>
              <span>•</span>
              <span>
                {message.metadata.generation_metadata.generation_time_ms}ms
              </span>
              {message.metadata.generation_metadata.context_chunks_used > 0 && (
                <>
                  <span>•</span>
                  <span>
                    {message.metadata.generation_metadata.context_chunks_used}{" "}
                    chunks
                  </span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex bg-[#0f172a]">
      {/* Sidebar - Hidden if hideChatSidebar is true */}
      {!hideChatSidebar && (
        <div className="w-80 bg-[#020617] border-r border-[#334155] flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-[#334155]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">
                Conversations
              </h2>
              <button
                onClick={() => createNewSession()}
                className="p-2 text-[#8b3dff] hover:text-purple-800 hover:bg-[#1e293b] rounded-lg transition-colors"
                title="New conversation"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>

            {/* Search stats */}
            {searchStats && (
              <div className="text-xs text-gray-500 flex items-center space-x-2">
                <span
                  className={`w-2 h-2 rounded-full ${
                    searchStats.canPerformSearch
                      ? "bg-green-500"
                      : "bg-yellow-500"
                  }`}
                ></span>
                <span>
                  {searchStats.canPerformSearch
                    ? `${searchStats.chunks_with_embeddings} chunks indexed`
                    : "Preparing knowledge base..."}
                </span>
              </div>
            )}
          </div>

          {/* Sessions List */}
          <div className="flex-1 overflow-y-auto">
            {isLoadingSessions ? (
              <div className="p-4 text-center">
                <LoadingSpinner size="sm" message="Loading conversations..." />
              </div>
            ) : sessions.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">No conversations yet</p>
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {sessions.map((session) => (
                  <div
                    key={session.id}
                    className={`group relative p-3 rounded-lg cursor-pointer transition-colors ${
                      currentSession?.id === session.id
                        ? "bg-[#1e293b] border border-[#334155]"
                        : "hover:bg-[#1e293b]"
                    }`}
                    onClick={() => switchSession(session)}
                  >
                    <div className="flex items-center justify-between">
                      {editingSession === session.id ? (
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          onBlur={() =>
                            updateSessionTitle(session.id, editTitle)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              updateSessionTitle(session.id, editTitle);
                            } else if (e.key === "Escape") {
                              setEditingSession(null);
                              setEditTitle("");
                            }
                          }}
                          className="text-sm font-medium text-white bg-transparent border-none outline-none w-full"
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <h3 className="text-sm font-medium text-white truncate pr-2">
                          {session.title}
                        </h3>
                      )}

                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSessionMenu((prev) => ({
                              ...prev,
                              [session.id]: !prev[session.id],
                            }));
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-gray-700 rounded transition-opacity"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>

                        {showSessionMenu[session.id] && (
                          <div className="absolute right-0 mt-1 w-48 bg-[#1e293b] rounded-md shadow-lg py-1 z-10 border border-[#334155]">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingSession(session.id);
                                setEditTitle(session.title);
                                setShowSessionMenu((prev) => ({
                                  ...prev,
                                  [session.id]: false,
                                }));
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-white hover:bg-[#020617]"
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Rename
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                archiveSession(session.id);
                                setShowSessionMenu((prev) => ({
                                  ...prev,
                                  [session.id]: false,
                                }));
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-white hover:bg-[#020617]"
                            >
                              <Archive className="w-4 h-4 mr-2" />
                              Archive
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteSession(session.id);
                                setShowSessionMenu((prev) => ({
                                  ...prev,
                                  [session.id]: false,
                                }));
                              }}
                              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-[#020617]"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-gray-500">
                        {session.message_count} messages
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(
                          session.last_message_at || session.created_at
                        ).toLocaleDateString()}
                      </span>
                    </div>

                    {session.metadata?.type && (
                      <div className="mt-1">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            session.metadata.type === "rag_chat"
                              ? "bg-purple-100 text-purple-800"
                              : session.metadata.type === "document_qa"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {session.metadata.type === "rag_chat"
                            ? "🧠"
                            : session.metadata.type === "document_qa"
                            ? "📄"
                            : "💬"}
                          {session.metadata.type.replace("_", " ")}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <Header
          title={currentSession?.title || "Slavo AI Assistant"}
          subtitle={
            searchStats?.canPerformSearch
              ? `Ready to help with your questions • ${searchStats.total_documents} documents indexed`
              : "Setting up knowledge base..."
          }
          showNavigation={!hideChatSidebar}
        />
        {/* Chat Controls */}
        <div className="bg-[#0f172a] border-b border-[#334155] px-6 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* RAG Mode Toggle */}

              {!hideChatMode && (
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">Mode:</label>
                  <select
                    value={ragMode ? "rag" : "chat"}
                    onChange={(e) => setRagMode(e.target.value === "rag")}
                    className="text-sm border border-[#334155] rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="rag">🤖 RAG (Document-aware)</option>
                    <option value="chat">💬 Chat Only</option>
                  </select>
                </div>
              )}

              {/* RAG Settings */}
              {ragMode && (
                <div className="flex items-center space-x-2">
                  <button
                    className="text-xs text-[#8b3dff] hover:text-purple-800 flex items-center space-x-1"
                    title="RAG Settings"
                  >
                    <Settings className="w-3 h-3" />
                    <Link to="/settings">Settings</Link>
                  </button>
                </div>
              )}

              {/* Search Status */}
              {searchStats && (
                <div className="text-xs text-gray-500 flex items-center space-x-2">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      searchStats.canPerformSearch
                        ? "bg-green-500"
                        : "bg-yellow-500"
                    }`}
                  ></span>
                  <span>
                    {searchStats.canPerformSearch
                      ? `${searchStats.chunks_with_embeddings} chunks ready`
                      : "Processing documents..."}
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => createNewSession()}
                className="flex items-center space-x-1 px-3 py-1 text-sm text-[#8b3dff] hover:text-purple-800 hover:bg-[#1e293b] rounded transition-colors"
                title="New conversation"
              >
                <Plus className="w-4 h-4" />
                <span>New</span>
              </button>
              {!hideExport && (
                <button
                  onClick={exportConversation}
                  disabled={!currentSession}
                  className="flex items-center space-x-1 px-3 py-1 text-sm text-green-600 hover:text-green-800 hover:bg-[#1e293b] rounded transition-colors disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  <span>Export</span>
                </button>
              )}

              {!hideSearch && (
                <button
                  onClick={() => (window.location.href = "/search")}
                  className="flex items-center space-x-1 px-3 py-1 text-sm text-purple-600 hover:text-purple-800 hover:bg-[#1e293b] rounded transition-colors"
                >
                  <Search className="w-4 h-4" />
                  <span>Search</span>
                </button>
              )}
            </div>
          </div>
        </div>
        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            {!currentSession ? (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 text-[#64748b] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">
                  Welcome to Slavo AI
                </h3>
                <p className="text-gray-600 mb-4">
                  Create a new conversation to get started
                </p>
                <button
                  onClick={() => createNewSession()}
                  className="bg-[#8b3dff] text-white px-3 py-2 rounded-md"
                >
                  Start New Conversation
                </button>
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-[#8b3dff] rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  <img src={AiChatIcon} alt="chat icon" className="bot-image" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">
                  Hi {user?.name}! 👋
                </h3>
                <p className="text-gray-600 mb-4">
                  I'm Slavo, your AI assistant for {user?.current_business}. I
                  can help you find information, answer questions about your
                  documents, and assist with various tasks.
                </p>
                <p className="text-sm text-gray-500">
                  {ragMode && searchStats?.canPerformSearch
                    ? `I have access to ${searchStats.total_documents} documents and can provide intelligent answers based on your knowledge base.`
                    : "RAG mode is enabled but I'm still processing your documents. You can still chat with me!"}
                </p>
              </div>
            ) : (
              messages.map(renderMessage)
            )}

            {/* Loading Indicator */}
            {isLoading && (
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white text-lg shadow-lg">
                  <img src={AiChatIcon} alt="chat icon" className="bot-image" />
                </div>
                <div className="max-w-xs lg:max-w-2xl">
                  <div className="bg-[#020617] border border-[#334155] px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                    <div className="flex items-center space-x-3">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                        <div
                          className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"
                          style={{ animationDelay: "0.1s" }}
                        ></div>
                        <div
                          className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"
                          style={{ animationDelay: "0.2s" }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600">
                        {ragMode && searchStats?.canPerformSearch
                          ? "Searching documents and generating response..."
                          : "Thinking..."}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
        {/* Message Input */}
        <div className="bg-[#1e293b] border-t border-[#334155] p-6">
          <div className="max-w-3xl mx-auto flex flex-col items-center">
            <form
              onSubmit={handleSendMessage}
              className="flex items-end justify-center w-full space-x-3"
            >
              <div className="flex-1 relative flex justify-center">
                <textarea
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    // auto-resize logic
                    const target = e.target;
                    target.style.height = "auto";
                    target.style.height = `${target.scrollHeight}px`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                  placeholder="Type your message here... (Shift+Enter for new line)"
                  className="bg-[#020617] text-white placeholder-gray-500 w-full min-h-[50px] max-h-[300px] border border-[#334155] rounded-2xl px-5 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-sm resize-none leading-relaxed overflow-hidden"
                  disabled={isLoading || !currentSession}
                  rows={1}
                />

                {/* Regenerate Button */}
                {messages.length > 0 &&
                  messages[messages.length - 1]?.role === "assistant" && (
                    <button
                      type="button"
                      onClick={regenerateMessage}
                      disabled={isLoading}
                      className="absolute right-5 bottom-4 text-gray-500 hover:text-gray-400 disabled:opacity-50 transition"
                      title="Regenerate response"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
              </div>

              {/* Send Button */}
              <button
                type="submit"
                disabled={!newMessage.trim() || isLoading || !currentSession}
                className="bg-[#8b3dff] text-white px-5 h-[50px] rounded-2xl hover:bg-purple-700  disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg flex items-center justify-center"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>

            {/* Debug Info */}
            {debugMode && (
              <div className="mt-3 text-center">
                <p className="text-xs text-gray-500">
                  {ragMode && searchStats?.canPerformSearch ? (
                    <>🧠 RAG mode enabled • Powered by your knowledge base</>
                  ) : ragMode ? (
                    <>⏳ RAG mode enabled • Processing your documents...</>
                  ) : (
                    <>💬 Chat mode • Direct conversation</>
                  )}
                </p>
                {currentSession && (
                  <p className="text-xs text-[#64748b] mt-1">
                    Session: {currentSession.title} • {messages.length} messages
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Agentic Auth Modal */}
      {agenticAuthState.showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-[#1e293b] rounded-lg shadow-xl p-6 w-full max-w-md border border-[#334155]">
            <h2 className="text-lg font-semibold text-white mb-4">
              Authentication Required
            </h2>

            <p className="text-sm text-gray-300 mb-4">
              This action requires special permissions. Please enter your access
              code to continue.
            </p>

            {agenticAuthState.error && (
              <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-md">
                <p className="text-sm text-red-300">{agenticAuthState.error}</p>
              </div>
            )}

            <input
              type="text"
              value={agenticAuthState.code}
              onChange={(e) =>
                setAgenticAuthState((prev) => ({
                  ...prev,
                  code: e.target.value,
                  error: "",
                }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && !agenticAuthState.loading) {
                  handleAgenticVerification();
                }
                if (e.key === "Escape") {
                  setAgenticAuthState((prev) => ({
                    ...prev,
                    showModal: false,
                  }));
                }
              }}
              className="w-full bg-[#020617] border border-[#334155] rounded-md px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500 text-white placeholder-gray-400"
              defaultValue="manifest-123456"
              placeholder="Enter access code"
              disabled={agenticAuthState.loading}
              autoFocus
            />

            <div className="flex justify-end space-x-3">
              <button
                onClick={() =>
                  setAgenticAuthState((prev) => ({ ...prev, showModal: false }))
                }
                disabled={agenticAuthState.loading}
                className="px-4 py-2 text-gray-300 hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAgenticVerification}
                disabled={
                  agenticAuthState.loading || !agenticAuthState.code.trim()
                }
                className="px-4 py-2 bg-[#8b3dff] text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {agenticAuthState.loading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                <span>
                  {agenticAuthState.loading ? "Verifying..." : "Verify"}
                </span>
              </button>
            </div>

            <p className="text-xs text-gray-400 mt-4">
              Access will be granted for 24 hours after successful verification.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
